import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import type {
  BottomTabBarProps,
  BottomTabNavigationOptions,
} from '@react-navigation/bottom-tabs';
import type {
  NativeStackHeaderProps,
  NativeStackNavigationOptions,
} from '@react-navigation/native-stack';
import { theme, TYPE_CONFIG, useResponsive } from '@sg/garnish';

import { useNavigateToLastInteractedStore } from '@order/LastInteractedStore';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { DesktopHeader } from '../components/Header/DesktopHeader';
import { useHeaderRight } from '../components/Header/Header.hooks';
import type {
  HeaderCloseIconItemProps,
  HeaderContentProps,
} from '../components/Header/Header.props';
import { MainTabBar } from '../components/MainTabBar';
import { getCustomBackButtonIcon } from './AppNavigation.backButton';
import type {
  AppStackParamList,
  AuthStackScreenProps,
  ModalStackScreenProps,
} from './AppNavigation.props';

// Screen options for the app stack.
export const useAppStackOptions = () => {
  const modalContentStyle = useMobileTransparentScreenContentStyle();
  const modalPresentationStyle = useMobileTransparentModalScreenPresentation();

  return useMemo<
    Record<keyof AppStackParamList | 'Navigator', NativeStackNavigationOptions>
  >(
    () => ({
      Navigator: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        headerShown: false,
      },
      MainTabs: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
      },
      Modal: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        contentStyle: modalContentStyle,
        presentation: modalPresentationStyle,
      },
      Auth: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        presentation: 'modal',
      },
      Error: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        presentation: 'fullScreenModal',
      },
    }),
    [modalContentStyle, modalPresentationStyle],
  );
};

// Screen options for the main tabs.
export const useMainTabsOptions = () => {
  const { t } = useLocalizationContext();

  // Bottom tabs only enabled on mobile views.
  const { currentBreakpoint } = useResponsive();
  const tabBarFn = useCallback(
    (props: BottomTabBarProps) => {
      return currentBreakpoint.isXS ? <MainTabBar {...props} /> : null;
    },
    [currentBreakpoint.isXS],
  );

  // Challenges/Sweetpass feature flag.
  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  // Menu tab action.
  const navigateToLastInteractedStore = useNavigateToLastInteractedStore();

  return useMemo(
    () => ({
      Navigator: {
        ...DEFAULT_TABS_OPTIONS,
      },
      TabBar: tabBarFn,
      HomeTab: {
        title: t('nav-bar.home.title'),
        tabBarTestID: 'nav-bar.home',
      },
      MenuTab: {
        title: t('nav-bar.menu.title'),
        tabBarTestID: 'nav-bar.order',
        customNavigate: navigateToLastInteractedStore,
      },
      LoyaltyTab: {
        title: isSweetpassEnabled
          ? t('nav-bar.sweetpass.title')
          : t('nav-bar.challenges.title'),
        tabBarTestID: 'nav-bar.loyalty',
      },
      AccountTab: {
        title: t('nav-bar.account.title'),
        tabBarTestID: 'nav-bar.account',
      },
    }),
    [t, tabBarFn, navigateToLastInteractedStore, isSweetpassEnabled],
  );
};

// Screen options for the home tab stack.
export const useHomeTabStackOptions = () => {
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      Home: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
    }),
    [navigatorScreenOptions],
  );
};

// Screen options for the menu tab stack.
export const useMenuTabStackOptions = () => {
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions();

  const defaultHeader = useStackHeader({ mobileHeaderShown: true });

  const defaultScreenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ header: defaultHeader }),
    [defaultHeader],
  );

  const menuHeader = useStackHeader({
    mobileHeaderShown: true,
    dividerHidden: true,
  });

  const menuHeaderScreenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ header: menuHeader }),
    [menuHeader],
  );

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      Locations: {
        ...defaultScreenOptions,
      },
      MenuWithoutSlug: {
        ...menuHeaderScreenOptions,
      },
      Menu: {
        ...menuHeaderScreenOptions,
      },
      DeliveryMenu: {
        ...menuHeaderScreenOptions,
      },
      MenuCollection: {
        ...defaultScreenOptions,
      },
      DeliveryMenuCollection: {
        ...defaultScreenOptions,
      },
    }),
    [defaultScreenOptions, menuHeaderScreenOptions, navigatorScreenOptions],
  );
};

// Screen options for the loyalty tab stack.
export const useLoyaltyTabStackOptions = () => {
  const { t } = useLocalizationContext();
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      SweetpassHome: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
      Challenges: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('challenges.signed-in.header'),
      },
    }),
    [t, navigatorScreenOptions],
  );
};

// Screen options for the loyalty tab stack.
export const useAccountTabStackOptions = () => {
  const { t } = useLocalizationContext();
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      AccountMenu: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
      Profile: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.profile.title'),
      },
      SweetpassMembership: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.sweetpass-membership.title'),
      },
      PaymentCredit: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.payment-promo.title'),
      },
      Addresses: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.addresses.title'),
      },
      Orders: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.orders.title'),
      },
      Favorites: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.favorites.title'),
      },
      ReferFriend: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.refer-friend.title'),
      },
      AccountDietaryRestrictions: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: t('account.dietary-restrictions.title'),
      },
    }),
    [t, navigatorScreenOptions],
  );
};

// Screen options for the modal stack.
export const useModalStackOptions = ({ navigation }: ModalStackScreenProps) => {
  const { t } = useLocalizationContext();

  const navigatorScreenOptions = useModalStackNavigatorScreenOptions({
    allowsTransparentModals: true,
    close: { onPress: navigation.goBack },
  });

  const defaultScreenOptions = useModalStackScreenOptions();

  const visibleHeader = useStackHeader({
    ...defaultScreenOptions,
    mobileHeaderShown: true,
  });

  const screenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ ...defaultScreenOptions, header: visibleHeader }),
    [defaultScreenOptions, visibleHeader],
  );

  const modalOptions = useMobileTransparentModalStackScreenOptions({
    ...defaultScreenOptions,
    mobileHeaderShown: true,
  });

  const bagScreenOptions = useMemo(
    () => ({
      ...defaultScreenOptions,
      ...modalOptions,
      headerShown: true,
    }),
    [defaultScreenOptions, modalOptions],
  );

  // Other than the navigator/default, all should either be:
  // - screenOptions - for when the screen is a screen.
  // - modalOptions - for when the screen is a modal.
  // - some edge case like the bag.
  return useMemo(
    () => ({
      Navigator: { ...navigatorScreenOptions },
      Default: { ...defaultScreenOptions },

      // Ordering flow is all visible headers.
      ProductDetails: { ...screenOptions },
      EditProductDetails: { ...screenOptions },
      DeliveryProductDetails: { ...screenOptions },
      DeliveryEditProductDetails: { ...screenOptions },
      Checkout: { ...screenOptions },
      OrderStatus: {
        ...screenOptions,
        headerBackVisible: false,
        headerLeft: () => null,
      },
      ShareLineItem: { ...screenOptions },

      // Bag is shown as a drawer on desktop.
      Bag: { ...bagScreenOptions },

      // Reorder flow shows as a transparent modal in desktop.
      ReorderConfirmLocation: { ...modalOptions },
      ReorderChangeLocation: { ...modalOptions },
      ReorderConflictReview: { ...modalOptions },
      ReorderActiveBagWarning: { ...modalOptions },
      RateOrder: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
        title: t('rate-order.title'),
      },

      // These are transparent forms used in desktop as well.
      DietaryRestrictions: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
      },

      // Scan To Pay flow is all visible headers, isn't used in desktop.
      ScanToPay: {
        ...screenOptions,
        headerShown: false,
        title: t('account.scan-to-pay.header.title'),
      },
      Payments: { ...screenOptions, title: t('account.scan-to-pay.payments') },
      CreditAdd: {
        ...screenOptions,
        title: t('account.scan-to-pay.promo-gift-code'),
      },

      // These are for the mobile account, aren't used in desktop.
      CreditDetails: {
        ...screenOptions,
        title: t('account.credit.mobile-label'),
      },
      PaymentCredit: {
        ...screenOptions,
        title: t('account.payment-promo.title'),
      },

      // OneTrust Personal Data Management
      PersonalData: {
        ...screenOptions,
        title: t('personal-data.title'),
      },
      PersonalDataDetails: { ...modalOptions },

      DeliveryOrderInFlight: { ...modalOptions },

      // Sweetpass
      SweetpassBenefits: {
        ...screenOptions,
        title: 'What you get with sweetpass+',
      },
      SweetpassUpgrade: {
        ...screenOptions,
        headerStyle: {
          backgroundColor: theme.colors.GREEN_5,
        },
        title: t('sweetpass-plus'),
      },
      SweetpassCheckout: {
        ...screenOptions,
        title: t('sweetpass-plus'),
      },
      SweetpassPaymentMethodEdit: {
        ...modalOptions,
        title: t('sweetpass.credit-card-form.edit'),
      },
      SweetpassPaymentMethodAdd: {
        ...modalOptions,
        title: t('sweetpass.credit-card-form.add'),
      },
      SweetpassBillingHistory: {
        ...modalOptions,
        title: t('account.sweetpass-membership.billing-history.header'),
      },

      // OrderProduct - Deep linking to order a product.
      OrderProductConfirmProduct: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
      },
      OrderProductConfirmLocation: { ...modalOptions },
      OrderProductSearchLocation: { ...modalOptions },
    }),
    [
      t,
      bagScreenOptions,
      defaultScreenOptions,
      navigatorScreenOptions,
      screenOptions,
      modalOptions,
    ],
  );
};

// Screen options for the auth stack.
export const useAuthStackOptions = ({ navigation }: AuthStackScreenProps) => {
  const navigatorScreenOptions = useModalStackNavigatorScreenOptions({
    mobileHeaderShown: true,
    close: { onPress: navigation.goBack },
  });

  return useMemo(
    () => ({
      Navigator: { ...navigatorScreenOptions },
      Login: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
      Join: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
      JoinVerification: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
      ForgotPassword: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
    }),
    [navigatorScreenOptions],
  );
};

// Screen options for error stack.
export const useErrorStackOptions = () => {
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: false,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      NotFound: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
    }),
    [navigatorScreenOptions],
  );
};

// ———————— STACK HEADER ——————————————————————

type NativeStackHeaderProp = NativeStackNavigationOptions['header'];

/**
 * Returns a function which may be passed to the `header` property
 * of a stack navigator's screen options.
 *
 * @param {HeaderContentProps} props - additional header content props
 */
const useStackHeader = (props?: UseStackHeaderProps): NativeStackHeaderProp => {
  const { currentBreakpoint } = useResponsive();
  const { mobileHeaderShown, ...headerContentProps } = props ?? {};

  if (currentBreakpoint.isXS && mobileHeaderShown) {
    return undefined;
  }

  if (currentBreakpoint.isXS && !mobileHeaderShown) {
    return () => <View />;
  }

  return (headerProps: NativeStackHeaderProps) => (
    <DesktopHeader {...headerProps} {...headerContentProps} />
  );
};

type UseStackHeaderProps = {
  readonly mobileHeaderShown?: boolean;
} & HeaderContentProps;

// ———————— CONTENT STYLE ——————————————————————

type ContentStyleOption = NativeStackNavigationOptions['contentStyle'];

const DEFAULT_CONTENT_STYLE: ContentStyleOption = {
  backgroundColor: theme.colors.KHAKI_3,
};

const TRANSPARENT_CONTENT_STYLE: ContentStyleOption = {
  backgroundColor: theme.colors.OPACITY.TRANSPARENT,
};

/**
 * The value of a `contentStyle` property for screens
 * which appear as "transparent modals" in the desktop form factor.
 */
const useMobileTransparentScreenContentStyle = (): ContentStyleOption => {
  const { currentBreakpoint } = useResponsive();

  return currentBreakpoint.isXS
    ? DEFAULT_CONTENT_STYLE
    : TRANSPARENT_CONTENT_STYLE;
};

type PresentationOption = NativeStackNavigationOptions['presentation'];

/**
 * The value of a 'presentation` property for screens
 * which appear as "transparent modals" on web.
 */
const useMobileTransparentModalScreenPresentation = (): PresentationOption => {
  const { currentBreakpoint } = useResponsive();

  return currentBreakpoint.isXS ? 'modal' : 'transparentModal';
};

const DEFAULT_HEADER_TITLE_STYLE = TYPE_CONFIG.BODY[
  '18'
] as NativeStackNavigationOptions['headerTitleStyle'];

// ———————— SCREEN OPTIONS ——————————————————————

/**
 * Default value of `screenOptions` for all screens.
 */
const DEFAULT_SCREEN_OPTIONS = {
  title: 'sweetgreen',
  headerBackImageSource: getCustomBackButtonIcon(),
};

/**
 * Default value of `screenOptions` for a native stack navigator.
 */
const DEFAULT_STACK_SCREEN_OPTIONS: NativeStackNavigationOptions = {
  ...DEFAULT_SCREEN_OPTIONS,
  headerShadowVisible: false,
  headerTintColor: theme.colors.GREEN_1,
  headerStyle: {
    backgroundColor: theme.colors.KHAKI_3,
  },
  headerTitleStyle: DEFAULT_HEADER_TITLE_STYLE,
  headerTitleAlign: 'center',
  headerBackTitleVisible: false,
  headerTitle: '',
  contentStyle: DEFAULT_CONTENT_STYLE,
};

const DEFAULT_TABS_OPTIONS: BottomTabNavigationOptions = {
  headerShown: false,
};

/**
 * The value of `screenOptions` for a native stack navigator contained in a tab.
 */
const useTabStackNavigatorScreenOptions = (
  props?: UseStackHeaderProps,
): NativeStackNavigationOptions => {
  const header = useStackHeader(props);

  return {
    ...DEFAULT_STACK_SCREEN_OPTIONS,
    headerShown: true,
    header,
  };
};

/**
 * The value of `screenOptions` for a native stack navigator contained in a modally presented screen.
 *
 * @param {Object} options - additional modal stack options
 * @param {HeaderCloseIconItemProps} options.close - Specifies the close button `onPress` handler, accessibility strings, and `testID`.
 * @param {boolean} options.allowsTransparentModals - If true, configures the contentStyle property to have a transparent background.
 */
const useModalStackNavigatorScreenOptions = ({
  close,
  allowsTransparentModals,
  ...stackHeaderProps
}: UseModalStackNavigatorScreenOptionsProps): NativeStackNavigationOptions => {
  const { currentBreakpoint } = useResponsive();
  const header = useStackHeader(stackHeaderProps);
  const headerRight = useHeaderRight({ close });
  const contentStyle =
    allowsTransparentModals && !currentBreakpoint.isXS
      ? TRANSPARENT_CONTENT_STYLE
      : DEFAULT_CONTENT_STYLE;
  const options: NativeStackNavigationOptions = {
    ...DEFAULT_STACK_SCREEN_OPTIONS,
    headerShown: true,
    header,
    headerRight,
    contentStyle,
  };

  return options;
};

type UseModalStackNavigatorScreenOptionsProps = {
  readonly close?: HeaderCloseIconItemProps;
  readonly allowsTransparentModals?: boolean;
} & UseStackHeaderProps;

/**
 * The value of `screenOptions` native stack navigator contained in a modally presented screen.
 */
const useModalStackScreenOptions = (): NativeStackNavigationOptions => {
  return {
    ...DEFAULT_SCREEN_OPTIONS,
    contentStyle: DEFAULT_CONTENT_STYLE,
  };
};

/**
 * The value of `options` for a stack screen presented as a "transparent modal" on web.
 */
const useMobileTransparentModalStackScreenOptions = (
  props?: UseStackHeaderProps,
): NativeStackNavigationOptions => {
  const { currentBreakpoint } = useResponsive();
  const header = useStackHeader(props);
  const contentStyle = useMobileTransparentScreenContentStyle();
  const options: NativeStackNavigationOptions = {
    ...DEFAULT_SCREEN_OPTIONS,
    presentation: currentBreakpoint.isXS ? 'card' : 'transparentModal',
    headerShown: currentBreakpoint.isXS,
    header,
    contentStyle,
  };

  return options;
};
